import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api/api';
import RoleBasedSidebar from '../../components/RoleBasedSidebar';
import '../../styles/CentersListPage.css';
import '../../styles/DashboardLayout.css';

const CentersListPage = () => {
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  useEffect(() => {
    fetchCenters();
  }, []);

  const fetchCenters = async () => {
    try {
      setLoading(true);
      const response = await api.get('/superadmin/centers');
      
      if (response.data && response.data.success) {
        setCenters(response.data.data);
      } else {
        setError('Failed to load centers data');
      }
    } catch (err) {
      console.error('Error fetching centers:', err);
      setError('Failed to load centers');
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort centers
  const filteredAndSortedCenters = centers
    .filter(center =>
      center.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      center.centerCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (center.admin?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'code':
          return a.centerCode.localeCompare(b.centerCode);
        case 'users':
          return (b.userCount || 0) - (a.userCount || 0);
        case 'classes':
          return (b.halaqaCount || 0) - (a.halaqaCount || 0);
        case 'created':
          return new Date(b.createdAt) - new Date(a.createdAt);
        default:
          return 0;
      }
    });

  const formatDate = (dateString) => {
    if (!dateString) return 'Not set';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="centers-list-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading centers...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="centers-list-page">
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={fetchCenters} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Dashboard Layout */}
      <div className="dashboard-layout">
        {/* Sidebar */}
        <RoleBasedSidebar isOpen={sidebarOpen} onClose={closeSidebar} />
        
        {/* Main Content */}
        <div className="dashboard-content">
          <div className="centers-list-page">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1>Centers Management</h1>
          <p className="subtitle">Manage all centers across the platform</p>
        </div>
        <div className="header-actions">
          <Link to="/centers/new" className="add-center-button">
            Add New Center
          </Link>
        </div>
      </div>

      {/* Controls */}
      <div className="centers-controls">
        <div className="search-section">
          <input
            type="text"
            placeholder="Search centers by name, code, or admin..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="controls-right">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="sort-select"
          >
            <option value="name">Sort by Name</option>
            <option value="code">Sort by Code</option>
            <option value="users">Sort by Users</option>
            <option value="classes">Sort by Classes</option>
            <option value="created">Sort by Created Date</option>
          </select>
          
          <div className="view-toggle">
            <button
              className={`view-button ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid View"
            >
              ⊞
            </button>
            <button
              className={`view-button ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="List View"
            >
              ☰
            </button>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="centers-stats">
        <div className="stat-card">
          <span className="stat-value">{centers.length}</span>
          <span className="stat-label">Total Centers</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">
            {centers.reduce((sum, center) => sum + (center.userCount || 0), 0)}
          </span>
          <span className="stat-label">Total Users</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">
            {centers.reduce((sum, center) => sum + (center.halaqaCount || 0), 0)}
          </span>
          <span className="stat-label">Total Classes</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">
            {centers.filter(center => center.admin?.name).length}
          </span>
          <span className="stat-label">Centers with Admins</span>
        </div>
      </div>

      {/* Centers Display */}
      {filteredAndSortedCenters.length === 0 ? (
        <div className="empty-state">
          <h3>No centers found</h3>
          <p>
            {searchTerm
              ? `No centers match "${searchTerm}"`
              : 'No centers have been created yet'}
          </p>
          {!searchTerm && (
            <Link to="/centers/new" className="add-center-button">
              Create First Center
            </Link>
          )}
        </div>
      ) : (
        <div className={`centers-container ${viewMode}`}>
          {filteredAndSortedCenters.map((center) => (
            <div key={center._id} className="center-card">
              <div className="center-header">
                <h3 className="center-name">{center.name}</h3>
                <span className="center-code">Code: {center.centerCode}</span>
              </div>
              
              <div className="center-info">
                <div className="admin-info">
                  <strong>Administrator:</strong>
                  <span>{center.admin?.name || 'No admin assigned'}</span>
                  {center.admin?.email && (
                    <span className="admin-email">{center.admin.email}</span>
                  )}
                </div>
                
                {center.location && (
                  <div className="location-info">
                    <strong>Location:</strong>
                    <span>{center.location}</span>
                  </div>
                )}
                
                <div className="contact-info">
                  {center.contactEmail && (
                    <div><strong>Email:</strong> {center.contactEmail}</div>
                  )}
                  {center.contactPhone && (
                    <div><strong>Phone:</strong> {center.contactPhone}</div>
                  )}
                  {center.website && (
                    <div><strong>Website:</strong> {center.website}</div>
                  )}
                </div>
              </div>

              <div className="center-stats">
                <div className="stat-item">
                  <span className="stat-number">{center.userCount || 0}</span>
                  <span className="stat-text">Users</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">{center.halaqaCount || 0}</span>
                  <span className="stat-text">Classes</span>
                </div>
              </div>

              <div className="center-meta">
                <span className="created-date">
                  Created: {formatDate(center.createdAt)}
                </span>
              </div>

              <div className="center-actions">
                <Link
                  to={`/center/details/${center._id}`}
                  className="action-button view-button"
                >
                  View Details
                </Link>
                <Link
                  to={`/center/edit/${center._id}`}
                  className="action-button edit-button"
                >
                  Edit
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CentersListPage;